import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, TrendingUp, AlertTriangle } from 'lucide-react';

// Typical UK private Mounjaro cost, halfway between the starter-dose (~£170)
// and top-dose (~£320) averages, September 2026.
const MONTHLY_COST = 245;
const MAX_MONTHS = 12;

const formatPounds = (value: number) => `£${value.toLocaleString('en-GB')}`;

interface MounjaroCounterProps {
  onCta: () => void;
}

export default function MounjaroCounter({ onCta }: MounjaroCounterProps) {
  const [open, setOpen] = useState(false);
  const [months, setMonths] = useState(1);
  const [shown, setShown] = useState(MONTHLY_COST);
  const shownRef = useRef(MONTHLY_COST);
  const revealed = months === MAX_MONTHS;
  const target = months * MONTHLY_COST;

  // Count the big number up/down smoothly instead of jumping.
  useEffect(() => {
    const start = shownRef.current;
    const diff = target - start;
    const startedAt = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / 450);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(start + diff * eased);
      shownRef.current = value;
      setShown(value);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="group relative w-full overflow-hidden rounded-3xl bg-gradient-to-br from-red-600 to-red-900 border-2 border-red-500 p-6 md:p-8 text-left shadow-[0_0_40px_-5px_rgba(220,38,38,0.6)] transition-all hover:-translate-y-1 hover:shadow-[0_0_60px_-5px_rgba(220,38,38,0.8)]"
      >
        <span className="absolute inset-0 rounded-3xl ring-4 ring-red-500/60 animate-pulse pointer-events-none"></span>
        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full animate-[shimmer_2.5s_infinite] pointer-events-none"></span>
        <div className="relative flex items-center gap-4">
          <div className="shrink-0 w-14 h-14 rounded-2xl bg-black/30 flex items-center justify-center text-3xl">💸</div>
          <div className="flex-1">
            <p className="text-white text-xl md:text-2xl font-black leading-tight">How much would Mounjaro <span className="underline decoration-wavy decoration-white/60 underline-offset-4">really</span> cost you?</p>
            <p className="text-white/80 text-sm md:text-base font-bold mt-1">Tap to find out</p>
          </div>
          <ArrowRight className="shrink-0 w-7 h-7 text-white transition-transform group-hover:translate-x-1" />
        </div>
      </button>
    );
  }

  return (
    <div className="rounded-3xl bg-white border-2 border-red-600 p-6 md:p-8 text-center shadow-[0_20px_50px_-15px_rgba(220,38,38,0.45)] animate-in fade-in zoom-in-95 duration-300">
      <p className="text-red-600 text-xs font-black uppercase tracking-[0.25em] mb-2">The real cost of Mounjaro</p>
      <p className="text-zinc-950 text-lg md:text-xl font-bold mb-6">
        Drag to see what you'd spend in <span className="text-red-600 font-black">{months} {months === 1 ? 'month' : 'months'}</span>
      </p>

      <label htmlFor="mounjaro-months" className="sr-only">Number of months on Mounjaro</label>
      <input
        id="mounjaro-months"
        type="range"
        min={1}
        max={MAX_MONTHS}
        step={1}
        value={months}
        onChange={(e) => setMonths(Number(e.target.value))}
        className="w-full h-3 cursor-pointer"
        style={{ accentColor: '#dc2626' }}
      />
      <div className="flex justify-between text-zinc-600 text-xs font-bold mt-2">
        <span>1 month</span>
        <span>12 months</span>
      </div>

      <div className="mt-8">
        <p className="text-5xl md:text-7xl font-black text-zinc-950 tracking-tight tabular-nums">{formatPounds(shown)}</p>
        <p className="text-zinc-600 text-sm md:text-base font-semibold mt-2 flex items-center justify-center gap-1.5">
          <TrendingUp className="w-4 h-4 text-red-600" /> spent on Mounjaro alone
        </p>
      </div>

      {!revealed && (
        <p className="text-zinc-600 text-sm font-semibold mt-6 animate-pulse">Keep going → drag to 12 months</p>
      )}

      {revealed && (
        <div className="mt-8 rounded-2xl bg-red-700 border-2 border-red-400 p-6 md:p-8 shadow-[0_20px_50px_-10px_rgba(220,38,38,0.7)] animate-in fade-in zoom-in-90 slide-in-from-bottom-4 duration-500">
          <AlertTriangle className="w-10 h-10 text-white mx-auto mb-3" />
          <p className="text-white/80 text-sm font-black uppercase tracking-widest mb-3">{formatPounds(MAX_MONTHS * MONTHLY_COST)} later…</p>
          <p className="text-white text-2xl md:text-3xl font-black leading-tight">
            In the end, you've not built the habits and the metabolism to keep the weight off.
          </p>
          <button
            onClick={onCta}
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap bg-white text-red-700 font-black uppercase tracking-wide md:tracking-wider text-sm md:text-base px-5 md:px-6 py-3 rounded-xl hover:bg-zinc-100 transition-all hover:-translate-y-0.5"
          >
            See If You Are A Fit <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}
